import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Check, Circle, FileText, Edit, Upload, Shield } from 'lucide-react';
import { PageWrapper } from '../components/PageWrapper';

export const Help = () => {
  const navigate = useNavigate();

  const sections = [
    {
      icon: Check,
      title: 'Goals & Home coach',
      content: `Goals live in Settings and on Home as a simple reminder of what you are rebuilding toward.

You can add or adjust goals in Settings, or ask your Home coach to help you phrase them. The coach can also suggest goals using in-chat commands you will not see as raw tags.

Older journal days may still show goal check-ins from before the conversational update; new days are centered on chat and History summaries.`
    },
    {
      icon: Edit,
      title: 'History & past days',
      content: `Open the History tab to browse past days. Tap an entry to read coach summaries, older journal text if you have it, and any notes you saved there.

You can add or update an optional day comment from that screen. Home is where you talk things through with your coach.`
    },
    {
      icon: FileText,
      title: 'Coach chat & journal',
      content: `Your coach on Home leads the conversation—there is no fixed list of reflection questions to manage.

Older days may still show legacy notes you typed before this update; new days are centered on chat and what you choose to save in History.`
    },
    {
      icon: Upload,
      title: 'Exporting Your Data',
      content: `You can export your data in multiple formats:

• CSV: A spreadsheet format for Excel or Google Sheets.

• PDF: A printable document with goal progress and your AI companion notes where saved.

• JSON Backup: A complete backup of all your data and settings. Use this to keep a backup, restore on a new device, or transfer between devices.

To restore, go to Export → Restore from Backup and select your JSON file.`
    },
    {
      icon: Shield,
      title: 'Data Privacy',
      content: `Your privacy is built into how the app works:

    • Journal data, goals, and coaching chat context are stored in your cloud account
    • Google sign-in is required in RE-BUILD-ME-PRO
    • No ad network tracking is used in the app

    If you delete the app, your cloud data remains on your account.
    You can remove account access from Settings with Delete Cloud Account.`
    },
  ];

  return (
    <PageWrapper className="bg-app">
      <div className="max-w-md mx-auto px-6 py-6">
        <div className="flex items-center gap-4 mb-8">
          <button
            onClick={() => navigate('/settings')}
            className="p-2 rounded-full hover:bg-wisteria-50 transition-colors"
            data-testid="back-btn"
            aria-label="Go back"
          >
            <ArrowLeft size={24} strokeWidth={1.5} className="text-secondary" />
          </button>
          <h1 className="font-heading text-2xl font-light text-primary" data-testid="help-title">
            Help & Instructions
          </h1>
        </div>

        <div className="space-y-4">
          {sections.map((section, index) => (
            <motion.div
              key={section.title}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className="bg-theme-light rounded-3xl p-6 shadow-ambient border border-theme-secondary"
              data-testid={`help-section-${index}`}
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-wisteria-100 flex items-center justify-center">
                  <section.icon size={20} style={{ color: 'var(--theme-accent, #6B5B95)' }} />
                </div>
                <h2 className="font-body font-medium text-primary">{section.title}</h2>
              </div>
              <p className="font-body text-secondary text-sm leading-relaxed whitespace-pre-line">
                {section.content}
              </p>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5 }}
          className="mt-8 text-center"
        >
          <p className="font-body text-sm text-secondary/60">
            RE-BUILD-ME — Your private daily reset
          </p>
        </motion.div>
      </div>
    </PageWrapper>
  );
};

export default Help;
